import { useMemo } from 'react';
import { useGradeConversionDetails } from '../context/GradeConversionDetailsContext';
import { useGradePointMappings } from '../data/useGradePointMappings';
import { GradeSchemaInfo, GradePointMapping } from '~/types/gradeConversion';
import { GradeConversion } from '../data/useGradeConversion';
import { useGradeConversionPageProps } from '~/data/useGradeConversionPageProps';
import {
  EducationLevelOption,
  CountryOption,
  mapEducationLevels,
  mapCountries,
} from '../../GradeConversion/utils/referenceData';

type Result =
  | {
      isLoading: true;
      error?: undefined;
      conversionTable?: undefined;
      gradeSchema?: undefined;
      gradePointMappings: GradePointMapping[];
      educationLevels: EducationLevelOption[];
      countries: CountryOption[];
    }
  | {
      isLoading: false;
      error?: Error;
      conversionTable?: GradeConversion;
      gradeSchema?: GradeSchemaInfo;
      gradePointMappings: GradePointMapping[];
      educationLevels: EducationLevelOption[];
      countries: CountryOption[];
    };

export function useGradeConversionPageData(): Result {
  const { conversionTable, conversionTableError, isConversionTableError, isLoadingConversionTable } =
    useGradeConversionDetails();
  const { gradePointMappings, isLoadingGradePointMappings, isGradePointMappingsError } = useGradePointMappings();
  const { initialProps } = useGradeConversionPageProps();

  const educationLevels = useMemo<EducationLevelOption[]>(
    () => mapEducationLevels(initialProps?.referenceData.educationLevels),
    [initialProps?.referenceData.educationLevels]
  );
  const countries = useMemo<CountryOption[]>(
    () => mapCountries(initialProps?.referenceData.countries),
    [initialProps?.referenceData.countries]
  );

  const gradeSchema = useMemo<GradeSchemaInfo | undefined>(() => {
    const gradeSchemaIncluded = conversionTable?.included?.find((inc) => inc.type === 'grade_schema');
    if (!gradeSchemaIncluded) {
      return undefined;
    }

    return {
      id: gradeSchemaIncluded.id,
      name: gradeSchemaIncluded.attributes.name,
      gradeSchemaType: gradeSchemaIncluded.attributes.gradeSchemaType as 'numeric' | 'letter',
      minGrade: gradeSchemaIncluded.attributes.minGrade,
      maxGrade: gradeSchemaIncluded.attributes.maxGrade,
      validGrades: gradeSchemaIncluded.attributes.validGrades,
    };
  }, [conversionTable]);

  if (isLoadingConversionTable || isLoadingGradePointMappings) {
    return {
      isLoading: true,
      gradePointMappings,
      educationLevels,
      countries,
    };
  }

  if (isConversionTableError) {
    return {
      isLoading: false,
      error: (conversionTableError as Error) ?? new Error('Failed to fetch the grade conversion details.'),
      gradePointMappings,
      educationLevels,
      countries,
    };
  }

  if (isGradePointMappingsError) {
    return {
      isLoading: false,
      error: new Error('Failed to load grade point mappings.'),
      conversionTable,
      gradeSchema,
      gradePointMappings,
      educationLevels,
      countries,
    };
  }

  if (!conversionTable) {
    return {
      isLoading: false,
      error: new Error('Grade conversion not found.'),
      gradePointMappings,
      educationLevels,
      countries,
    };
  }

  if (!gradeSchema) {
    return {
      isLoading: false,
      error: new Error('Grade schema not found.'),
      conversionTable,
      gradePointMappings,
      educationLevels,
      countries,
    };
  }

  if (!gradePointMappings.length) {
    return {
      isLoading: false,
      error: new Error('Grade point mappings not found.'),
      conversionTable,
      gradeSchema,
      gradePointMappings,
      educationLevels,
      countries,
    };
  }

  return {
    isLoading: false,
    conversionTable,
    gradeSchema,
    gradePointMappings,
    educationLevels,
    countries,
  };
}
